import { ProductSelectionGroup } from '@brenger/api-client';

/**
 * Product groups from the API embed a fully resolved `parent`, and nested
 * `size_variant` and `material` lists point back at those same objects.
 * JSON.stringify then recurses until iOS/WebKit throws
 * "RangeError: Maximum call stack size exceeded" (other engines throw TypeError).
 * That happens while saving the item form and while redux-persist writes the flow.
 *
 * This returns a deep copy where object `parent` links are IRI strings and any
 * other repeated object is replaced by its IRI, so the result can be serialized.
 */
export const sanitizeProductGroup = (group: ProductSelectionGroup): ProductSelectionGroup => {
  const seen = new WeakSet<object>();
  const json = JSON.stringify(group, (key, value) => {
    if (key === 'parent' && typeof value === 'object' && value !== null) {
      return typeof value.iri === 'string' ? value.iri : null;
    }
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return typeof value.iri === 'string' ? value.iri : undefined;
      }
      seen.add(value);
    }
    return value;
  });
  return JSON.parse(json) as ProductSelectionGroup;
};

type PsProperties = {
  size_variant?: ProductSelectionGroup[];
  size_variant_choice?: ProductSelectionGroup;
  material?: ProductSelectionGroup[];
  material_choice?: ProductSelectionGroup[];
};

type PsTempWithGroups = {
  group: ProductSelectionGroup | null;
  properties?: PsProperties;
};

const sanitizeGroupList = (groups?: ProductSelectionGroup[]): ProductSelectionGroup[] | undefined => {
  return groups?.map(group => sanitizeProductGroup(group));
};

/** Strip cycles from the product groups stored on a transport-request item. */
export const sanitizeStoredItem = <T extends { ps_temp: PsTempWithGroups }>(item: T): T => {
  const properties = item.ps_temp.properties;
  return {
    ...item,
    ps_temp: {
      ...item.ps_temp,
      group: item.ps_temp.group ? sanitizeProductGroup(item.ps_temp.group) : item.ps_temp.group,
      properties: properties
        ? {
            ...properties,
            size_variant: sanitizeGroupList(properties.size_variant),
            size_variant_choice: properties.size_variant_choice
              ? sanitizeProductGroup(properties.size_variant_choice)
              : properties.size_variant_choice,
            material: sanitizeGroupList(properties.material),
            material_choice: sanitizeGroupList(properties.material_choice),
          }
        : properties,
    },
  };
};
